import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Divider, 
  Card, 
  CardContent, 
  CardActions, 
  Button,
  Grid,
  Tabs,
  Tab, 
  Alert,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import OperationForm from './OperationForm';

const DEFAULT_OPERATION = {
  name: '',
  prompt: '',
  format: ': "{text}" Output as JSON, using this template: {\'output\': \'improved text\'}',
  model: 'llama3.1:latest',
  provider: 'ollama',
  linesPerGroup: 5
};

const OperationManager = ({ 
  operations = [],
  onAdd,
  onUpdate,
  onDelete,
  onClose
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [newOperation, setNewOperation] = useState(DEFAULT_OPERATION);
  const [editingOperation, setEditingOperation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      await onAdd(newOperation);
      setNewOperation(DEFAULT_OPERATION);
    } catch (err) {
      setError(err.message || 'Error al añadir operación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      await onUpdate(editingOperation.name, editingOperation);
      setEditingOperation(null);
    } catch (err) {
      setError(err.message || 'Error al actualizar operación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`¿Está seguro que desea eliminar la operación "${name}"?`)) {
      return;
    }
    
    setError('');
    
    try {
      await onDelete(name);
    } catch (err) {
      setError(err.message || 'Error al eliminar operación');
    }
  };

  const startEditing = (operation) => {
    setEditingOperation({
      ...operation,
      editMode: true
    });
  };

  return (
    <Box sx={{ 
      position: 'absolute', 
      top: '50%', 
      left: '50%', 
      transform: 'translate(-50%, -50%)',
      width: { xs: '90%', sm: '80%', md: '70%' },
      maxHeight: '90vh',
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: 24,
      p: 3,
      overflow: 'auto'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Gestionar Operaciones
        </Typography>
        <IconButton onClick={onClose} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab label="Añadir Nueva" />
          <Tab label="Operaciones Existentes" />
        </Tabs>
      </Box>
      
      <Box hidden={tabValue !== 0} sx={{ p: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          Añadir Nueva Operación
        </Typography>
        <OperationForm 
          operation={newOperation}
          onChange={setNewOperation}
          onSubmit={handleAddSubmit}
          submitLabel="Añadir Operación"
          isSubmitting={submitting}
        />
      </Box>
      
      <Box hidden={tabValue !== 1} sx={{ p: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          Operaciones Existentes
        </Typography>
        
        {operations.length === 0 ? (
          <Alert severity="info">No hay operaciones configuradas.</Alert>
        ) : (
          <Grid container spacing={2}>
            {operations.map((op) => (
              <Grid item xs={12} sm={6} md={4} key={op.name}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" component="div">
                      {op.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Modelo: {op.model}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      <strong>Prompt:</strong> {op.prompt}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      <strong>Formato:</strong> {op.format}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Proveedor:</strong> {op.provider}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Líneas por grupo:</strong> {op.linesPerGroup}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<EditIcon />}
                      onClick={() => startEditing(op)}
                    >
                      Editar
                    </Button>
                    <Button 
                      size="small" 
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(op.name)}
                    >
                      Eliminar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        
        {editingOperation && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6">
              Editar Operación: {editingOperation.name}
            </Typography>
            <OperationForm 
              operation={editingOperation}
              onChange={setEditingOperation}
              onSubmit={handleUpdateSubmit}
              submitLabel="Actualizar"
              onCancel={() => setEditingOperation(null)}
              isSubmitting={submitting}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OperationManager; 